import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  InputNumber,
  Upload,
  Image,
  message,
  Select,
  Table,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { fetchContainers } from "./FetchContainers";
import { EditOutlined } from "@ant-design/icons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { token } from "../../constants/Constants";
import SecureImage from "../wellnessExecutive/SecureImage";
import { create_container, update_container } from "../../endpoints/API";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const Containers = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const isUser = useSelector((state) => state.user.user);
  const [containers, setContainers] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    const getContainers = async () => {
      try {
        await fetchContainers(setContainers, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(error?.response?.data?.error || "Failed to fetch containers.");
      }
    };
    getContainers();
  }, []);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedContainer(null);
    form.resetFields();
    setFileList([]);
  };

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    try {
      const base64 = await getBase64(file);
      onSuccess("ok");
    } catch (error) {
      onError(error);
    }
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files.");
    }
    return isImage;
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const handleOnFinish = async (values) => {
    try {
      const base64Files = await Promise.all(
        fileList
          .filter((file) => file.originFileObj)
          .map((file) => getBase64(file.originFileObj))
      );

      const payload = {
        ...values,
        profile_pic: base64Files[0] || null,
        created_by: isUser?.id,
      };

      const response = selectedContainer
        ? await update_container(selectedContainer.id, payload)
        : await create_container(payload);

      const responseData = response?.data;

      if (response.statusText == "OK") {
        message.success(
          selectedContainer
            ? "Container updated successfully!"
            : "Container created successfully!"
        );
        setIsModalOpen(false);
        setSelectedContainer(null);
        form.resetFields();
        setFileList([]);
        await fetchContainers(setContainers, baseUrl, prodTestUrl);
      } else {
        message.error(responseData.error || "Failed to save container.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error);
    }
  };

  const handleEdit = (record) => {
    setSelectedContainer(record);
    form.setFieldsValue(record);
    setFileList([]);
    showModal();
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Priority", dataIndex: "priority", key: "priority" },
    { title: "Color", dataIndex: "color", key: "color" },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (text) => (
        <SecureImage path={text} width={100} />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Button onClick={() => handleEdit(record)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      {/* Replaced position:absolute button with flex header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <Button type="primary" onClick={showModal}>
          Create Container
        </Button>
      </div>

      {/* Scrollable table wrapper for mobile */}
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          dataSource={containers.containers}
          columns={columns}
          rowKey={(record) => record.id}
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title={selectedContainer ? "Edit Container" : "Create Container"}
        visible={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        // Responsive modal width
        width="min(520px, 95vw)"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <Form form={form} layout="vertical" onFinish={handleOnFinish}>
          <Form.Item label="Image">
            <Upload
              customRequest={handleCustomRequest}
              listType="picture-circle"
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              onPreview={handlePreview}
              maxCount={1}
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </Form.Item>

          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter a name" }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            label="Priority"
            name="priority"
            rules={[{ required: true, message: "Please enter a priority" }]}
          >
            <InputNumber
              min={1}
              placeholder="Enter priority"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item label="Color" name="color">
            <Select
              placeholder="Select color"
              options={[
                { label: "Red",    value: "red" },
                { label: "Green",  value: "green" },
                { label: "Blue",   value: "blue" },
                { label: "Yellow", value: "yellow" },
                { label: "Purple", value: "purple" },
                { label: "Orange", value: "orange" },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              {selectedContainer ? "Update" : "Submit"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Containers;